// Startsidans tRAT-lista: urval, sortering och "Väntar på dig".
// Ren logik utan React, så den går att testa för sig.

// Datum som tRAT:en "hör till": när den är schemalagd att köras, annars när
// den skapades. En tRAT schemalagd till nästa vecka ska inte hamna längst ner
// bara för att den skapades för en månad sedan.
export function sessionDate(s) {
  return new Date(s.availableAt || s.createdAt).getTime() || 0;
}

// Pågående först, sedan schemalagda med den närmaste först, sedan resten
// nyast först. Schemalagda i fallande ordning gav den som ligger längst bort
// överst — den man letar efter är nästa.
export function sortSessions(sessions, statusFor) {
  const rank = s => (statusFor(s) === "live" ? 0 : statusFor(s) === "scheduled" ? 1 : 2);
  return [...sessions].sort((a, b) => {
    const ra = rank(a), rb = rank(b);
    if (ra !== rb) return ra - rb;
    if (ra === 1) return sessionDate(a) - sessionDate(b);
    return sessionDate(b) - sessionDate(a);
  });
}

// Kursvalen i filtret: de kurser som faktiskt har tRAT:ar i listan.
// instanceCourse: course_instance_id → { id, name }
export function courseOptions(sessions, instanceCourse) {
  const byId = new Map();
  for (const s of sessions) {
    const c = instanceCourse[s.courseInstanceId];
    if (c && !byId.has(c.id)) byId.set(c.id, c);
  }
  return [...byId.values()].sort((a, b) => (a.name || "").localeCompare(b.name || "", "sv"));
}

// filters: { course: "all" | courseId, owner: "all" | "mine", status: "all" | status, q }
export function filterSessions(sessions, filters, { userId, statusFor, instanceCourse, searchText }) {
  const q = (filters.q || "").trim().toLowerCase();
  return sessions.filter(s => {
    if (filters.owner === "mine" && s.teacherId !== userId) return false;
    if (filters.course && filters.course !== "all" && instanceCourse[s.courseInstanceId]?.id !== filters.course) return false;
    if (filters.status && filters.status !== "all" && statusFor(s) !== filters.status) return false;
    // "Alla" visar inte avslutade (de finns under Avslutade) — utom vid sökning,
    // så att en avslutad tRAT går att hitta på namn utan att byta flik.
    if (filters.status === "all" && !q && statusFor(s) === "locked") return false;
    if (!q) return true;
    return searchText(s).toLowerCase().includes(q);
  });
}

// Den senaste tRAT:en som har körts klart (minst en färdig grupp, ingen som
// skrapar just nu) — underlaget för "Senast avslutad" på startsidan.
export function lastFinishedSession(sessions, statusFor, hasFinishedGroups) {
  return sessions
    .filter(s => ["done", "locked"].includes(statusFor(s)) && hasFinishedGroups(s))
    .sort((a, b) => sessionDate(b) - sessionDate(a))[0] || null;
}

// Nästa schemalagda tRAT, för "Pågår nu" när inget pågår.
export function nextScheduled(sessions, now = Date.now()) {
  return sessions
    .filter(s => s.availableAt && !s.sessionLocked && new Date(s.availableAt).getTime() > now)
    .sort((a, b) => new Date(a.availableAt) - new Date(b.availableAt))[0] || null;
}

// Färdiga gruppers resultat → snitt, procent per grupp och den fråga som
// färst grupper klarade på första försöket (4 poäng). rows: dbLoadAllResults.
export function summarizeResults(rows, numQ) {
  const done = rows.filter(r => !r.inProgress && r.maxScore > 0);
  if (done.length === 0) return null;
  const groups = done
    .map(r => ({ name: r.groupName, pct: Math.round((100 * (r.total || 0)) / r.maxScore) }))
    .sort((a, b) => b.pct - a.pct);
  const avgPct = Math.round(groups.reduce((sum, g) => sum + g.pct, 0) / groups.length);
  let hardest = null;
  for (let qi = 0; qi < numQ; qi++) {
    const firstTry = done.filter(r => r.scratchState?.[qi]?.score === 4).length;
    if (!hardest || firstTry < hardest.firstTry) hardest = { qi, firstTry };
  }
  // Klarade alla grupper allt på första försöket finns ingen "svåraste fråga"
  if (hardest && hardest.firstTry === done.length) hardest = null;
  return { groups, avgPct, groupCount: done.length, hardest };
}

// Filtren minns mellan besöken (per webbläsare). Sökfältet sparas inte.
const FILTER_KEY = "trat.home.filters";
export function loadSavedFilters() {
  try {
    const v = JSON.parse(localStorage.getItem(FILTER_KEY) || "{}");
    return {
      course: typeof v.course === "string" ? v.course : "all",
      owner: v.owner === "mine" ? "mine" : "all",
    };
  } catch {
    return { course: "all", owner: "all" };
  }
}
export function saveFilters({ course, owner }) {
  try { localStorage.setItem(FILTER_KEY, JSON.stringify({ course, owner })); } catch { /* privat läge */ }
}
